/**
 * The shell: the floating sidebar (src/layouts/Sidebar.jsx: brand, tenant,
 * task-based navigation, existing reports) and a floating sticky top bar with
 * the filters and the signed-in user (UserMenu.jsx) above the content.
 * Below desktop width the sidebar is a drawer opened from the menu button
 * (mockup screen 14); it closes when a page is chosen.
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { routeLocation } from "../../shared/catalog.js";
import { platformsInclude } from "../../shared/tenants.js";
import { FilterBar } from "../components/FilterBar.jsx";
import { IconButton } from "../components/ui/index.js";
import { EmptyContext } from "../lib/emptyContext.js";
import { useFilters } from "../lib/useFilters.js";
import { filtersFor } from "../lib/tabFilters.js";
import { useTenant } from "../tenant/TenantProvider.jsx";
import { Sidebar } from "./Sidebar.jsx";
import { UserMenu } from "./UserMenu.jsx";

export function AppShell({ children }) {
  // On a content details page (/content/:type/:id) the type's tab stays active.
  const { dashboard, tab } = routeLocation(useParams());
  const { search, pathname } = useLocation();
  const f = useFilters();
  const [navOpen, setNavOpen] = useState(false);
  // A page change closes the drawer.
  useEffect(() => setNavOpen(false), [pathname]);
  // Empty cards say "no matching items" (with Clear filters) only when this page's filters are set.
  const filtered = f.pageKeys.some((k) => f.filters[k]);
  const empty = useMemo(() => ({ filtered, clear: f.clear }), [filtered, f.clear]);
  // The App / Web choice only means something for a tenant live on both.
  const { current } = useTenant();
  const both = platformsInclude(current.platforms, "web") && platformsInclude(current.platforms, "app");
  const show = filtersFor(dashboard, tab).filter((g) => both || g !== "platform");

  return (
    <div className="flex min-h-screen bg-surface-0">
      <Sidebar current={`${dashboard}/${tab}`} search={search} mobileOpen={navOpen} onClose={() => setNavOpen(false)} />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* The page shows through around the bar while it sticks. */}
        <header className="no-print sticky top-0 z-30 bg-surface-0/85 px-3 pt-3 backdrop-blur-md lg:px-5">
          <div className="mx-auto flex max-w-[1600px] items-center gap-2 rounded-card bg-surface-1 py-2 pr-2 pl-2 shadow-card lg:pl-3">
            <IconButton icon="menu" label="Open navigation" aria-expanded={navOpen} onClick={() => setNavOpen(true)} className="text-ink lg:hidden" />
            <div className="min-w-0 flex-1">
              <FilterBar f={f} show={show} />
            </div>
            <span aria-hidden className="mx-1 h-7 w-px bg-line max-sm:hidden" />
            <UserMenu />
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1600px] flex-1 px-3 py-5 lg:px-5 lg:py-6">
          <EmptyContext.Provider value={empty}>{children}</EmptyContext.Provider>
        </main>
      </div>
    </div>
  );
}
